// src/utils/stats.js
import { startOfWeek, startOfMonth, format } from 'date-fns';

export function calcStats(trades, settings = {}) {
  // Exclude open/active trades from closed-trade statistics
  const closedTrades = (trades || []).filter(t => t.status !== 'open');
  if (!closedTrades || closedTrades.length === 0) {
    return {
      totalTrades: 0, wins: 0, losses: 0, breakevens: 0,
      winRate: 0, totalPnl: 0, avgRR: 0, profitFactor: 0,
      currentBalance: settings.startingBalance || 0,
      dailyPnl: 0, weeklyPnl: 0, monthlyPnl: 0,
      maxDrawdown: 0, currentDrawdown: 0, dailyPct: 0, weeklyPct: 0, monthlyPct: 0,
      ruleFollowPct: 0, bestDay: null, worstDay: null,
      bestDayPnl: 0, worstDayPnl: 0
    };
  }

  // Use local date strings for comparison — fixes today/week not updating
  const now        = new Date();
  const todayStr   = format(now, 'yyyy-MM-dd');
  const weekStart  = format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const monthStart = format(startOfMonth(now), 'yyyy-MM-dd');

  const wins       = trades.filter(t => t.result === 'Win');
  const losses     = trades.filter(t => t.result === 'Loss');
  const totalPnl   = trades.reduce((s, t) => s + Number(t.pnl || 0), 0);
  const grossProfit= wins.reduce((s, t) => s + Number(t.pnl || 0), 0);
  const grossLoss  = Math.abs(losses.reduce((s, t) => s + Number(t.pnl || 0), 0));

  // Fix: compare date strings directly — no timezone issues
  const dailyPnl   = trades.filter(t => t.date === todayStr)
                            .reduce((s, t) => s + Number(t.pnl || 0), 0);
  const weeklyPnl  = trades.filter(t => t.date >= weekStart)
                            .reduce((s, t) => s + Number(t.pnl || 0), 0);
  const monthlyPnl = trades.filter(t => t.date >= monthStart)
                            .reduce((s, t) => s + Number(t.pnl || 0), 0);

  // Equity / drawdown
  const startBal = settings.startingBalance || 0;
  // Subtract withdrawals from balance but not from stats
  const totalWithdrawals = (settings.withdrawals || [])
    .reduce((s, w) => s + Number(w.amount || 0), 0);

  let peak = startBal, maxDd = 0, running = startBal;
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  for (const t of sorted) {
    running += Number(t.pnl || 0);
    if (running > peak) peak = running;
    const dd = peak - running;
    if (dd > maxDd) maxDd = dd;
  }
  const currentBalance = startBal + totalPnl - totalWithdrawals;
  const currentDrawdown = Math.max(0, peak - (startBal + totalPnl));

  // Best/worst day
  // SEMANTIC RULE: best day = the most profitable WINNING day, worst day =
  // the most unprofitable LOSING day. A winning day is never a "worst" day
  // (even if it's the only trading day on record), so we restrict each side
  // to its appropriate sign. If no losing days exist yet, worstDay stays
  // null and the dashboard treats it as "no data".
  const byDay = {};
  for (const t of closedTrades) {
    if (!byDay[t.date]) byDay[t.date] = 0;
    byDay[t.date] += Number(t.pnl || 0);
  }
  const dayEntries = Object.entries(byDay);
  let bestDay = null, worstDay = null, bestDayPnl = 0, worstDayPnl = 0;
  for (const [d, v] of dayEntries) {
    // Only consider POSITIVE days as candidates for "best"
    if (v > 0 && v > bestDayPnl)  { bestDayPnl  = v; bestDay  = d; }
    // Only consider NEGATIVE days as candidates for "worst"
    if (v < 0 && v < worstDayPnl) { worstDayPnl = v; worstDay = d; }
  }

  const validRRs = closedTrades.filter(t => t.rrAchieved != null && t.rrAchieved !== '');
  const avgRR    = validRRs.length
    ? validRRs.reduce((s, t) => s + Number(t.rrAchieved), 0) / validRRs.length : 0;

  const ruleFollowed   = closedTrades.filter(t => t.followedRules === true || t.followedRules === 'true');
  const ruleFollowPct  = trades.length ? Math.round((ruleFollowed.length / trades.length) * 100) : 0;

  // ROI % = (current balance - starting balance) / starting balance * 100
  const roi = startBal > 0 ? Number(((currentBalance - startBal) / startBal * 100).toFixed(2)) : 0;
  // Daily/Weekly/Monthly % returns
  const dailyPct   = startBal > 0 ? Number(((dailyPnl   / startBal) * 100).toFixed(2)) : 0;
  const weeklyPct  = startBal > 0 ? Number(((weeklyPnl  / startBal) * 100).toFixed(2)) : 0;
  const monthlyPct = startBal > 0 ? Number(((monthlyPnl / startBal) * 100).toFixed(2)) : 0;
  const totalPnlPct= startBal > 0 ? Number(((totalPnl   / startBal) * 100).toFixed(2)) : 0;

  return {
    totalTrades: closedTrades.length,
    wins: wins.length, losses: losses.length,
    breakevens: closedTrades.filter(t => t.result === 'Breakeven').length,
    winRate:    closedTrades.length ? Math.round((wins.length / closedTrades.length) * 100) : 0,
    totalPnl, totalPnlPct, roi,
    avgRR: Number(avgRR.toFixed(2)),
    profitFactor: grossLoss > 0 ? Number((grossProfit / grossLoss).toFixed(2)) : grossProfit > 0 ? '∞' : 0,
    currentBalance: Number(currentBalance.toFixed(2)),
    dailyPnl, weeklyPnl, monthlyPnl,
    dailyPct, weeklyPct, monthlyPct,
    maxDrawdown: Number(maxDd.toFixed(2)),
    currentDrawdown: Number(currentDrawdown.toFixed(2)),
    ruleFollowPct,
    bestDay, worstDay,
    bestDayPnl:  Number(bestDayPnl),
    worstDayPnl: Number(worstDayPnl),
    totalWithdrawals
  };
}

export function getEquityCurveData(trades, startingBalance = 10000) {
  if (!trades.length) return [];
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  let balance = startingBalance;
  return sorted.map(t => {
    balance += Number(t.pnl || 0);
    return { date: t.date, balance: Number(balance.toFixed(2)), pnl: Number(t.pnl || 0) };
  });
}

export function getPnlByDayData(trades) {
  const byDay = {};
  for (const t of trades) {
    if (!byDay[t.date]) byDay[t.date] = 0;
    byDay[t.date] += Number(t.pnl || 0);
  }
  return Object.entries(byDay)
    .sort((a, b) => new Date(a[0]) - new Date(b[0]))
    .map(([date, pnl]) => ({ date, pnl: Number(pnl.toFixed(2)) }));
}

export function getMistakeData(trades) {
  const counts = {};
  for (const t of trades) {
    const m = t.mistakeMade || 'None';
    if (m && m !== 'None' && m !== '') counts[m] = (counts[m] || 0) + 1;
  }
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({ name, value }));
}

export function getTodayTrades(trades) {
  const today = format(new Date(), 'yyyy-MM-dd');
  return trades.filter(t => t.date === today);
}
